import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RefreshCw, DollarSign, Activity, TrendingUp, Zap, Database } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const APIUsageMonitor = () => {
  const [usageSummary, setUsageSummary] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState('all');

  useEffect(() => {
    fetchUsageData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchUsageData, 30000);
    return () => clearInterval(interval);
  }, [selectedProvider]);

  const fetchUsageData = async () => {
    try {
      const provider = selectedProvider === 'all' ? '' : selectedProvider;
      
      const [summaryRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/api-usage/summary`, {
          params: { provider },
          withCredentials: true
        }),
        axios.get(`${API}/admin/api-usage/logs`, {
          params: { provider, limit: 100 },
          withCredentials: true
        })
      ]);
      
      setUsageSummary(summaryRes.data);
      setRecentLogs(logsRes.data.logs || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch API usage data:', error);
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchUsageData();
    toast.success('Usage data refreshed');
  };

  if (loading && !usageSummary) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const summary = usageSummary?.summary || {};
  const byProvider = usageSummary?.by_provider || {};
  const byModel = usageSummary?.by_model || {};
  const dailyUsage = usageSummary?.daily_usage || {};

  return (
    <div className="space-y-6">
      {/* Header with Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">API Usage & Cost Tracking</h2>
          <p className="text-slate-600 text-sm mt-1">
            Monitor Perplexity and OpenAI API consumption, tokens, and costs
          </p>
        </div>
        <Button onClick={handleRefresh} variant="outline" size="sm">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Provider Filter */}
      <div className="flex gap-2">
        <Button
          variant={selectedProvider === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedProvider('all')}
        >
          All Providers
        </Button>
        <Button
          variant={selectedProvider === 'perplexity' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedProvider('perplexity')}
        >
          Perplexity
        </Button>
        <Button
          variant={selectedProvider === 'openai' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedProvider('openai')}
        >
          OpenAI
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Total API Calls
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{summary.total_calls || 0}</div>
            <p className="text-xs text-green-600 mt-1">
              ✓ {summary.successful_calls || 0} successful
            </p>
            {summary.failed_calls > 0 && (
              <p className="text-xs text-red-600">
                ✗ {summary.failed_calls} failed
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Database className="w-4 h-4" />
              Total Tokens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{(summary.total_tokens || 0).toLocaleString()}</div>
            <p className="text-xs text-slate-500 mt-1">
              Avg: {summary.average_tokens_per_call || 0} per call
            </p>
            <div className="flex gap-2 mt-2 text-xs">
              <span className="text-blue-600">↑ {(summary.total_input_tokens || 0).toLocaleString()} in</span>
              <span className="text-green-600">↓ {(summary.total_output_tokens || 0).toLocaleString()} out</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Total Cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              ${(summary.total_cost_usd || 0).toFixed(4)}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Avg: ${(summary.average_cost_per_call || 0).toFixed(4)} per call
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Success Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {summary.total_calls > 0
                ? ((summary.successful_calls / summary.total_calls) * 100).toFixed(1)
                : 0}%
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {summary.successful_calls || 0} / {summary.total_calls || 0} calls
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Provider Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Usage by Provider</CardTitle>
          <CardDescription>Token and cost breakdown by API provider</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(byProvider).map(([provider, data]) => (
              <div key={provider} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge variant={provider === 'perplexity' ? 'default' : 'secondary'}>
                      {provider.toUpperCase()}
                    </Badge>
                    <span className="text-sm text-slate-600">
                      {data.calls} calls ({data.successful} successful, {data.failed} failed)
                    </span>
                  </div>
                  <div className="text-lg font-bold text-green-600">
                    ${data.cost.toFixed(4)}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-600">Input Tokens</p>
                    <p className="font-semibold">{data.input_tokens.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Output Tokens</p>
                    <p className="font-semibold">{data.output_tokens.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Total Tokens</p>
                    <p className="font-semibold">{data.total_tokens.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Model Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Usage by Model</CardTitle>
          <CardDescription>API calls and costs per model</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Model</TableHead>
                <TableHead className="text-right">Calls</TableHead>
                <TableHead className="text-right">Total Tokens</TableHead>
                <TableHead className="text-right">Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(byModel).map(([model, data]) => (
                <TableRow key={model}>
                  <TableCell className="font-medium">{model}</TableCell>
                  <TableCell className="text-right">{data.calls}</TableCell>
                  <TableCell className="text-right">{data.total_tokens.toLocaleString()}</TableCell>
                  <TableCell className="text-right text-green-600 font-semibold">
                    ${data.cost.toFixed(4)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Daily Usage */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Usage (Last 7 Days)</CardTitle>
          <CardDescription>Daily API consumption trends</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(dailyUsage).map(([date, data]) => (
              <div key={date} className="flex items-center justify-between border-b pb-2">
                <div>
                  <p className="font-medium">{new Date(date).toLocaleDateString()}</p>
                  <p className="text-xs text-slate-600">{data.calls} API calls</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{data.tokens.toLocaleString()} tokens</p>
                  <p className="text-sm text-green-600">${data.cost.toFixed(4)}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent API Calls Log */}
      <Card>
        <CardHeader>
          <CardTitle>Recent API Calls</CardTitle>
          <CardDescription>Last 100 API requests with details</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-h-96 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead className="text-right">Tokens</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-xs">
                      {new Date(log.created_at).toLocaleTimeString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={log.provider === 'perplexity' ? 'default' : 'secondary'} className="text-xs">
                        {log.provider}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{log.model}</TableCell>
                    <TableCell className="text-right text-xs">{log.total_tokens}</TableCell>
                    <TableCell className="text-right text-xs text-green-600">
                      ${log.cost_usd.toFixed(4)}
                    </TableCell>
                    <TableCell>
                      {log.success ? (
                        <Badge variant="outline" className="text-green-600 text-xs">Success</Badge>
                      ) : (
                        <Badge variant="destructive" className="text-xs">Failed</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default APIUsageMonitor;
